/**
 * AppLayout - main application layout with responsive header and navigation
 */

import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ResponsiveHeader from './ResponsiveHeader';
import BottomNav from './BottomNav';
import UnifiedAIChatButton from '@components/ai/UnifiedAIChatButton';

export default function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Navigation handlers for chat entity links
  const handleTaskClick = (taskId: string, boardId: string) => {
    navigate(`/boards/${boardId}?task=${taskId}`);
  };

  const handleBoardClick = (boardId: string) => {
    navigate(`/boards/${boardId}`);
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleMeetingClick = (meetingId: string) => {
    navigate(`/meetings/${meetingId}`);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950">
      {/* Skip to main content link for keyboard/screen-reader users */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-neutral-900 focus:shadow-lg dark:focus:bg-neutral-800 dark:focus:text-neutral-100"
      >
        {t('common.skipToContent', 'Skip to content')}
      </a>

      {/* Subtle grain texture overlay */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-30 dark:opacity-20 bg-grain" />

      {/* Main layout container */}
      <div className="relative z-10">
        <ResponsiveHeader />

        {/* Main content area with smooth transitions.
            pb-20 clears the fixed BottomNav (lg:hidden); removed above lg. */}
        <main id="main" className="page-transition pb-20 lg:pb-0">
          <Outlet />
        </main>

        {/* Bottom navigation for mobile */}
        <BottomNav />

        {/* Global AI Chat Button - available on all pages */}
        <UnifiedAIChatButton
          onTaskClick={handleTaskClick}
          onBoardClick={handleBoardClick}
          onProjectClick={handleProjectClick}
          onMeetingClick={handleMeetingClick}
        />
      </div>
    </div>
  );
}
